import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import api from '../api/config';

// Firebase configuration - you need to replace this with your actual Firebase config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging
const messaging = getMessaging(app);

// Function to request notification permission, get token and send it to backend
export const requestNotificationPermission = async () => {
  try {
    // Check if the browser supports notifications
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return null;
    }
    
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return null;
    }
    
    // Get registration token
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

// Send the token to your backend
export const sendTokenToServer = async (token) => {
  if (!token) return false;
  
  try {
    // Get the authentication token (JWT)
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      console.error('User not authenticated');
      return false;
    }

    // Send the token to your backend
    await api.post('/user/notification/token', 
      { token, deviceType: getDeviceType() },
      {
        headers: {
          'Authorization': `Bearer ${atob(authToken)}`
        }
      }
    );

    return true;
  } catch (error) {
    console.error('Error sending token to server:', error);
    return false;
  }
};

// Register the service worker for background notifications
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/'
      });
      return registration;
    } catch (error) {
      console.error('Service worker registration failed:', error);
      return null;
    }
  } else {
    return null;
  }
};

// Handle foreground messages
export const setupForegroundMessaging = () => {
  onMessage(messaging, (payload) => {
    // Display the notification using the Notification API
    const title = payload.data.title;
    const body = payload.data.body;
    const icon = '/img/favicon/android-chrome-512x512.png';
    
    // Display a custom notification
    if (Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: icon || '/img/favicon/android-chrome-512x512.png'
      });
    }
  });
};

// Initialize Firebase messaging and set up listeners
export const initializeFirebaseMessaging = async () => {
  try {
    // Register service worker for background notifications
    await registerServiceWorker();
    
    // Request permission and get token
    const token = await requestNotificationPermission();
    
    // Send token to server if we have one
    if (token) {
      await sendTokenToServer(token);
    }
    
    // Setup handling of foreground messages
    setupForegroundMessaging();
    
    return token;
  } catch (error) {
    console.error('Error initializing Firebase messaging:', error);
    return null;
  }
}; 

function getDeviceType() {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
      return "WINDOWS_PHONE";
  }

  if (/android/i.test(userAgent)) {
      return "ANDROID";
  }

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return "IOS";
  }

  return "WEB";
}